import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import DateTimePicker from "./components/DateTimePicker";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Kontakt – SPDB Studio Zürich | Diskrete Anfrage",
  "description": "Kontaktieren Sie SPDB Studio für diskretes Single Property Branding. Jede Anfrage wird vertraulich behandelt. Antwort innerhalb von 24 Stunden.",
  "url": `${siteUrl}/kontakt`,
  "publisher": {
    "@type": "Organization",
    "name": "SPDB Studio",
    "url": siteUrl,
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "studio@sbdm-studio.ch",
      "telephone": "+41766827078",
      "contactType": "customer service",
      "availableLanguage": ["German", "English", "French"],
      "areaServed": "CH"
    }
  }
};

const interests = [
  "Objekt-Veredelung",
  "Demo anfragen",
  "Partnerschaft",
  "Allgemeine Anfrage",
];

export default function KontaktPage() {
  const [selected, setSelected] = useState("Objekt-Veredelung");
  const [charCount, setCharCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [wunschtermin, setWunschtermin] = useState("");
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const { ref, isVisible } = useScrollReveal(0.05);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    try {
      await fetch("https://readdy.ai/api/form/d6vvuqtt4s7j0tv5iklg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <>
      <title>Kontakt – Diskrete Anfrage | SPDB Studio Zürich</title>
      <meta name="description" content="Kontaktieren Sie SPDB Studio für diskretes Single Property Branding in der Schweiz. Anfragen für Luxusimmobilien ab 2 Mio. CHF — vertraulich, persönlich, innerhalb 24 Stunden." />
      <meta name="keywords" content="SPDB Studio Kontakt, Single Property Branding Anfrage, Luxusimmobilien Marketing Zürich, diskrete Anfrage" />
      <link rel="canonical" href={`${siteUrl}/kontakt`} />
      <JsonLd data={contactSchema} />
      <main className="min-h-screen bg-white">
        <Navbar />

        {/* Header */}
        <section className="pt-48 pb-16 px-8 lg:px-16 bg-warm-white">
          <div className="max-w-2xl mx-auto text-center">
            <p className="section-label mb-6">Diskrete Anfrage</p>
            <h1 className="font-serif text-5xl md:text-6xl text-text-primary font-medium leading-tight mb-6">
              Kontakt
            </h1>
            <span className="gold-divider mb-8" />
            <p className="font-sans font-light text-text-secondary text-base leading-relaxed">
              Jede Anfrage wird mit absoluter Vertraulichkeit behandelt. Wir melden uns innerhalb von 24 Stunden — persönlich.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-24 px-8 lg:px-16">
          <div
            ref={ref}
            className={`max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 transition-all duration-800 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Left info */}
            <div className="lg:col-span-2 flex flex-col justify-between">
              <div>
                <h2 className="font-serif text-3xl text-text-primary font-medium leading-tight mb-6">
                  Lassen Sie uns zusammenarbeiten.
                </h2>
                <p className="font-sans font-light text-text-secondary text-sm leading-relaxed mb-10">
                  Sie haben ein Objekt, das mehr verdient als ein Standardinserat. Wir haben das Know-how, es in eine digitale Ikone zu verwandeln.
                </p>

                <div className="flex flex-col gap-8">
                  <div>
                    <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-2">E-Mail</p>
                    <a
                      href="mailto:studio@sbdm-studio.ch"
                      className="font-sans text-text-primary text-sm hover:text-gold transition-colors duration-300"
                    >
                      studio@sbdm-studio.ch
                    </a>
                  </div>
                  <div>
                    <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-2">Standort</p>
                    <p className="font-sans text-text-primary text-sm">Zürich, Schweiz</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-2">Reaktionszeit</p>
                    <p className="font-sans text-text-primary text-sm">Innerhalb von 24 Stunden</p>
                  </div>
                </div>
              </div>

              <blockquote className="mt-16 pl-6 border-l-2 border-gold">
                <p className="font-serif text-xl italic text-text-secondary leading-relaxed">
                  „Exzellenz braucht keine Lautstärke. Sie spricht für sich."
                </p>
              </blockquote>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-24 text-center">
                  <div className="w-16 h-16 flex items-center justify-center border border-gold mb-8">
                    <i className="ri-check-line text-gold text-2xl" />
                  </div>
                  <h3 className="font-serif text-3xl text-text-primary font-medium mb-4">
                    Vielen Dank.
                  </h3>
                  <p className="font-sans font-light text-text-secondary text-sm leading-relaxed max-w-xs">
                    Ihre Anfrage ist eingegangen. Wir melden uns innerhalb von 24 Stunden persönlich bei Ihnen.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-8"
                >
                  {/* Interest tags */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-4">
                      Ihr Anliegen
                    </label>
                    <input type="hidden" name="anliegen" value={selected} />
                    <div className="flex flex-wrap gap-3">
                      {interests.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => setSelected(tag)}
                          className={`text-xs tracking-luxury font-sans font-medium uppercase px-5 py-2 border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                            selected === tag
                              ? "border-gold bg-gold/5 text-gold"
                              : "border-light-gray text-text-muted hover:border-gold hover:text-gold"
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                        Vorname
                      </label>
                      <input
                        type="text"
                        name="vorname"
                        required
                        placeholder="Thomas"
                        className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                        Nachname
                      </label>
                      <input
                        type="text"
                        name="nachname"
                        required
                        placeholder="Müller"
                        className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                      E-Mail-Adresse
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="t.mueller@makler.ch"
                      className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300"
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                      Telefon (optional)
                    </label>
                    <input
                      type="tel"
                      name="telefon"
                      placeholder="+41 76 682 70 78"
                      className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300"
                    />
                  </div>

                  {/* Objekt */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                      Objekt / Marktwert (optional)
                    </label>
                    <input
                      type="text"
                      name="objekt"
                      placeholder="z.B. Villa Zürichsee, CHF 4.8 Mio."
                      className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300"
                    />
                  </div>

                  {/* Wunschtermin */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-4">
                      Wunschtermin für Rückruf (optional)
                    </label>
                    <input type="hidden" name="wunschtermin" value={wunschtermin} />
                    <DateTimePicker onChange={setWunschtermin} />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-xs tracking-luxury font-sans text-text-muted uppercase block mb-3">
                      Ihre Nachricht
                    </label>
                    <textarea
                      name="nachricht"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Bitte beschreiben Sie kurz Ihr Anliegen..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border-b border-light-gray bg-transparent py-3 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors duration-300 resize-none"
                    />
                    <p className="text-xs font-sans text-text-muted mt-1 text-right">{charCount}/500</p>
                  </div>

                  {/* Datenschutz Checkbox */}
                  <div className="flex items-start gap-4">
                    <button
                      type="button"
                      onClick={() => setPrivacyChecked(!privacyChecked)}
                      className={`flex-shrink-0 w-5 h-5 border mt-0.5 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                        privacyChecked
                          ? "border-gold bg-gold/10"
                          : "border-light-gray hover:border-gold"
                      }`}
                      aria-label="Datenschutz akzeptieren"
                    >
                      {privacyChecked && (
                        <i className="ri-check-line text-gold text-xs" />
                      )}
                    </button>
                    <p className="font-sans font-light text-text-muted text-xs leading-relaxed">
                      Ich habe die{" "}
                      <Link
                        to="/datenschutz"
                        target="_blank"
                        className="text-gold hover:opacity-70 transition-opacity duration-300 underline underline-offset-2"
                      >
                        Datenschutzbestimmungen
                      </Link>{" "}
                      gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu.{" "}
                      <span className="text-text-muted/60">*</span>
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={!privacyChecked}
                    className={`btn-gold-fill self-start transition-opacity duration-300 ${
                      !privacyChecked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                    }`}
                  >
                    Diskrete Anfrage absenden
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}